import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import {
  SUPPORTED_CURRENCIES,
  getCurrencySymbol,
} from '../../utils/invoice-pdf-general-section-utilities';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import {
  useInvoiceItems,
  useCreateInvoiceItem,
  useUpdateInvoiceItem,
  useDeleteInvoiceItem,
  useInvoiceItemCategories,
} from '../../hooks/useBilling';
import { useInvoices } from '../../hooks/useBilling';
import SmartDataGrid from '../common/SmartDataGrid';
import { handleApiError } from '../../utils/errorHandler';

const InvoiceItemsPanel = () => {
  const { t, i18n } = useTranslation('business');
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const locale = i18n?.language || 'en-US';

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Multi-currency support state
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  
  // Format currency using selected currency from invoice PDF utilities
  const formatCurrency = useCallback((amount) => {
    const symbol = getCurrencySymbol(selectedCurrency);
    return `${symbol}${Number(amount || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [selectedCurrency, locale]);

  const [itemForm, setItemForm] = useState({
    invoice_id: '',
    description: '',
    quantity: 1,
    unit_price: 0,
    category: '',
    tax_rate: 20,
  });

  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices();
  const { data: itemsData, isLoading: itemsLoading, refetch: refetchItems } = useInvoiceItems(selectedInvoice?.id);
  const { data: categoriesData } = useInvoiceItemCategories();

  const createItemMutation = useCreateInvoiceItem();
  const updateItemMutation = useUpdateInvoiceItem();
  const deleteItemMutation = useDeleteInvoiceItem();

  const invoices = useMemo(() => invoicesData?.items || invoicesData || [], [invoicesData]);
  const items = useMemo(() => itemsData?.items || itemsData || [], [itemsData]);
  const _categories = useMemo(() => categoriesData?.items || categoriesData || [], [categoriesData]);

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item =>
      item.description?.toLowerCase().includes(query) ||
      item.category?.toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  const getCategoryColor = (category) => {
    const colors = {
      picking: 'primary',
      receiving: 'secondary',
      packing: 'info',
      storage: 'warning',
      return: 'error',
      inventory: 'success',
      insurance: 'default',
      management: 'primary',
      it: 'secondary',
    };
    return colors[category?.toLowerCase()] || 'default';
  };

  const handleCreateItem = async () => {
    try {
      await createItemMutation.mutateAsync({
        ...itemForm,
        invoice_id: selectedInvoice?.id || itemForm.invoice_id,
      });
      enqueueSnackbar(t('invoicing.items.messages.createSuccess'), { variant: 'success' });
      setCreateDialogOpen(false);
      setItemForm({ invoice_id: '', description: '', quantity: 1, unit_price: 0, category: '', tax_rate: 20 });
      refetchItems();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error creating invoice item:');
    }
  };

  const handleUpdateItem = async () => {
    try {
      await updateItemMutation.mutateAsync({ itemId: selectedItem.id, data: itemForm });
      enqueueSnackbar(t('invoicing.items.messages.updateSuccess'), { variant: 'success' });
      setEditDialogOpen(false);
      refetchItems();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error updating invoice item:');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm(t('invoicing.items.messages.deleteConfirm'))) {
      try {
        await deleteItemMutation.mutateAsync(itemId);
        enqueueSnackbar(t('invoicing.items.messages.deleteSuccess'), { variant: 'success' });
        refetchItems();
      } catch (error) {
        handleApiError(error, enqueueSnackbar, t, 'Error deleting invoice item:');
      }
    }
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setItemForm({
      invoice_id: item.invoice_id,
      description: item.description || '',
      quantity: item.quantity || 1,
      unit_price: item.unit_price || 0,
      category: item.category || '',
      tax_rate: item.tax_rate || 20,
    });
    setEditDialogOpen(true);
  };

  const invoiceColumns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'invoice_number', headerName: t('invoicing.items.columns.invoiceNumber'), flex: 1 },
    { 
      field: 'status', 
      headerName: t('invoicing.items.columns.status'), 
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color={params.value === 'paid' ? 'success' : 'default'} />
      ),
    },
        { 
          field: 'total_amount', 
          headerName: t('invoicing.items.columns.total'), 
          width: 120,
          renderCell: (params) => formatCurrency(params.value),
        },
  ];

  const itemColumns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'description', headerName: t('invoicing.items.columns.description'), flex: 1.5 },
    { 
      field: 'category', 
      headerName: t('invoicing.items.columns.category'), 
      width: 130,
      renderCell: (params) => (
        <Chip 
          label={t(`invoicing.items.categories.${params.value?.toLowerCase()}`, params.value)} 
          size="small" 
          color={getCategoryColor(params.value)}
        />
      ),
    },
    { 
      field: 'quantity', 
      headerName: t('invoicing.items.columns.quantity'), 
      width: 100,
      type: 'number',
    },
        { 
          field: 'unit_price', 
          headerName: t('invoicing.items.columns.unitPrice'), 
          width: 120,
          renderCell: (params) => formatCurrency(params.value),
        },
        { 
          field: 'total', 
          headerName: t('invoicing.items.columns.lineTotal'), 
          width: 120,
          renderCell: (params) => formatCurrency((params.row.quantity || 0) * (params.row.unit_price || 0)),
        },
    {
      field: 'actions',
      headerName: t('invoicing.items.columns.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('invoicing.items.editItem')}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(params.row); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('invoicing.items.deleteItem')}>
            <IconButton 
              size="small" 
              color="error" 
              onClick={(e) => { e.stopPropagation(); handleDeleteItem(params.row.id); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{t('invoicing.items.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('invoicing.items.subtitle')}</Typography>
        </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* Currency Selector */}
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>{t('invoicing.items.currency', 'Currency')}</InputLabel>
                    <Select
                      value={selectedCurrency}
                      label={t('invoicing.items.currency', 'Currency')}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                    >
                      {SUPPORTED_CURRENCIES.slice(0, 20).map((curr) => (
                        <MenuItem key={curr} value={curr}>
                          {getCurrencySymbol(curr)} {curr}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder={t('invoicing.items.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetchItems()}>
                    {t('common.refresh', 'Refresh')}
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => setCreateDialogOpen(true)}
                    disabled={!selectedInvoice}
                  >
                    {t('invoicing.items.newItem')}
                  </Button>
                </Box>
      </Box>

      <Box sx={{ flexGrow: 1, display: 'flex' }}>
        <Box sx={{ width: selectedInvoice ? '40%' : '100%', borderRight: selectedInvoice ? 1 : 0, borderColor: 'divider' }}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <Typography variant="subtitle2">{t('invoicing.items.selectInvoice')}</Typography>
          </Box>
          <SmartDataGrid
            rows={invoices}
            columns={invoiceColumns}
            loading={invoicesLoading}
            onRowClick={(params) => setSelectedInvoice(params.row)}
            getRowClassName={(params) => params.row.id === selectedInvoice?.id ? 'Mui-selected' : ''}
          />
        </Box>

        {selectedInvoice && (
          <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Typography variant="subtitle1">
                {t('invoicing.items.itemsFor')} {selectedInvoice.invoice_number || `INV-${selectedInvoice.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredItems.length} {t('invoicing.items.itemCount')}
              </Typography>
            </Box>
            {itemsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredItems.length === 0 ? (
              <Alert severity="info" sx={{ m: 2 }}>{t('invoicing.items.noItems')}</Alert>
            ) : (
              <SmartDataGrid
                rows={filteredItems}
                columns={itemColumns}
                loading={itemsLoading}
              />
            )}
          </Box>
        )}
      </Box>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.items.newItem')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('invoicing.items.columns.description')}
              value={itemForm.description}
              onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>{t('invoicing.items.columns.category')}</InputLabel>
              <Select
                value={itemForm.category}
                label={t('invoicing.items.columns.category')}
                onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
              >
                {['picking', 'receiving', 'packing', 'storage', 'return', 'inventory', 'insurance', 'management', 'IT'].map(cat => (
                  <MenuItem key={cat} value={cat}>
                    {t(`invoicing.items.categories.${cat}`, cat)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('invoicing.items.columns.quantity')}
              type="number"
              value={itemForm.quantity}
              onChange={(e) => setItemForm({ ...itemForm, quantity: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              label={t('invoicing.items.columns.unitPrice')}
              type="number"
              value={itemForm.unit_price}
              onChange={(e) => setItemForm({ ...itemForm, unit_price: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label={t('invoicing.items.columns.taxRate')}
              type="number"
              value={itemForm.tax_rate}
              onChange={(e) => setItemForm({ ...itemForm, tax_rate: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.1 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleCreateItem}>{t('common.create', 'Create')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.items.editItem')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('invoicing.items.columns.description')}
              value={itemForm.description}
              onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>{t('invoicing.items.columns.category')}</InputLabel>
              <Select
                value={itemForm.category}
                label={t('invoicing.items.columns.category')}
                onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
              >
                {['picking', 'receiving', 'packing', 'storage', 'return', 'inventory', 'insurance', 'management', 'IT'].map(cat => (
                  <MenuItem key={cat} value={cat}>
                    {t(`invoicing.items.categories.${cat}`, cat)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('invoicing.items.columns.quantity')}
              type="number"
              value={itemForm.quantity}
              onChange={(e) => setItemForm({ ...itemForm, quantity: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              label={t('invoicing.items.columns.unitPrice')}
              type="number"
              value={itemForm.unit_price}
              onChange={(e) => setItemForm({ ...itemForm, unit_price: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label={t('invoicing.items.columns.taxRate')}
              type="number"
              value={itemForm.tax_rate}
              onChange={(e) => setItemForm({ ...itemForm, tax_rate: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.1 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleUpdateItem}>{t('common.save', 'Save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(InvoiceItemsPanel);
